// src/components/agent/Timeline.js
// Vertical deal-flow timeline. Each step shows:
//  - a numbered dot (✓ when complete)
//  - a connecting line down to the next step
//  - a title + sub
//  - an italic "verbal note" (the teaching breadcrumb pulled from the
//    training transcript)
//  - the step's body content
import React, { useState } from 'react';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_PALE = '#F7F0DC';
const BORDER = '#E5E7EB';
const MID = '#4B5563';
const LIGHT = '#9CA3AF';

const STATUS_STYLES = {
  done:    { bg: '#DCFCE7', fg: '#166534', border: '#86EFAC', line: '#86EFAC' },
  active:  { bg: BURG,      fg: '#FFFFFF', border: BURG,      line: BURG },
  pending: { bg: '#F3F4F6', fg: '#9CA3AF', border: '#E5E7EB', line: '#E5E7EB' }
};

export default function Timeline({ steps = [], defaultOpenIndex = 0 }) {
  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    steps.forEach((s, i) => { init[s.id] = i !== defaultOpenIndex && !s.alwaysOpen; });
    return init;
  });
  const toggle = (id) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

  return (
    <div style={t.wrap}>
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const isCollapsed = collapsed[step.id];
        const status = step.status || 'pending';
        const sc = STATUS_STYLES[status] || STATUS_STYLES.pending;

        return (
          <div key={step.id} style={t.row}>
            <div style={t.rail}>
              <div style={{
                ...t.dot,
                background: sc.bg,
                color: sc.fg,
                borderColor: sc.border,
                ...(status === 'active' ? { boxShadow: `0 0 0 4px rgba(107,30,46,0.12)` } : {})
              }}>
                {status === 'done' ? '✓' : (i + 1)}
              </div>
              {!isLast && <div style={{ ...t.line, background: sc.line }} />}
            </div>

            <section style={isCollapsed ? t.card : { ...t.card, ...t.cardOpen, ...(status === 'active' ? t.cardActive : {}) }}>
              <button
                type="button"
                onClick={() => toggle(step.id)}
                style={t.head}
                aria-expanded={!isCollapsed}
              >
                <div style={t.headLeft}>
                  <div style={t.title}>
                    <span>{step.title}</span>
                    {status === 'active' && <span style={t.badgeActive}>In progress</span>}
                    {status === 'done' && <span style={t.badgeDone}>Done</span>}
                  </div>
                  {step.sub && <div style={t.sub}>{step.sub}</div>}
                </div>
                <div style={t.headRight}>
                  {step.summary && <span style={t.summary}>{step.summary}</span>}
                  <span style={{ ...t.chevron, transform: isCollapsed ? 'rotate(0)' : 'rotate(180deg)' }}>⌄</span>
                </div>
              </button>

              {!isCollapsed && (
                <div style={t.body}>
                  {step.note && (
                    <div style={t.note}>
                      <span style={t.noteIcon}>💡</span>
                      <span>{step.note}</span>
                    </div>
                  )}
                  {step.content}
                </div>
              )}
            </section>
          </div>
        );
      })}
    </div>
  );
}

const t = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 4, position: 'relative', fontFamily: "'Inter', sans-serif" },
  row: { display: 'grid', gridTemplateColumns: '44px 1fr', gap: 14, alignItems: 'stretch' },

  rail: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, paddingTop: 20 },
  dot: {
    width: 32, height: 32, borderRadius: '50%',
    border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 700,
    flexShrink: 0, zIndex: 2, transition: 'background 0.2s ease, box-shadow 0.2s ease'
  },
  line: { width: 2, flex: 1, marginTop: 4, marginBottom: -4, minHeight: 24, alignSelf: 'stretch', borderRadius: 2 },

  card: {
    background: '#fff', border: `1px solid ${BORDER}`,
    borderRadius: 12, marginBottom: 16, overflow: 'hidden',
    flex: 1, minWidth: 0,
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease'
  },
  cardOpen: { boxShadow: '0 1px 3px rgba(17,24,39,0.05), 0 1px 2px rgba(17,24,39,0.04)' },
  cardActive: { borderColor: 'rgba(107,30,46,0.35)', boxShadow: '0 4px 16px rgba(107,30,46,0.08)' },

  head: {
    width: '100%', background: 'transparent', border: 'none',
    padding: '16px 20px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 14, cursor: 'pointer',
    fontFamily: "'Inter', sans-serif", textAlign: 'left'
  },
  headLeft: { minWidth: 0, flex: 1 },
  title: {
    fontFamily: "'Manrope', sans-serif", fontSize: 16, fontWeight: 700,
    color: '#111827', letterSpacing: '-0.01em', lineHeight: 1.3,
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
  },
  badgeActive: {
    fontSize: 11, fontWeight: 600, color: BURG, background: 'rgba(107,30,46,0.08)',
    padding: '2px 8px', borderRadius: 6, fontFamily: "'Inter', sans-serif", letterSpacing: 0
  },
  badgeDone: {
    fontSize: 11, fontWeight: 600, color: '#166534', background: '#DCFCE7',
    padding: '2px 8px', borderRadius: 6, fontFamily: "'Inter', sans-serif", letterSpacing: 0
  },
  sub: { fontSize: 13, color: MID, marginTop: 4, lineHeight: 1.5 },
  headRight: { display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 },
  summary: {
    fontSize: 12, color: LIGHT,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220
  },
  chevron: {
    fontSize: 14, color: LIGHT, transition: 'transform 0.2s ease',
    display: 'inline-block', width: 14, textAlign: 'center', fontWeight: 700
  },

  body: {
    borderTop: `1px solid ${BORDER}`,
    padding: '20px', background: '#FAFAFA'
  },
  note: {
    background: '#FFFBEB', border: `1px solid ${GOLD_PALE}`,
    borderRadius: 10, padding: '12px 14px', marginBottom: 18,
    display: 'flex', alignItems: 'flex-start', gap: 10,
    color: '#78350F', fontSize: 13, lineHeight: 1.6
  },
  noteIcon: { fontSize: 14, lineHeight: 1.4, flexShrink: 0 }
};
